import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  authLink,
  clearPurchaseIntent,
  leadReturnPath,
  readPurchaseIntent,
  samePage,
  savePurchaseIntent,
} from '@/lib/purchaseIntent';
import {
  MapPin,
  DollarSign,
  CalendarDays,
  Lock,
  Phone,
  Mail,
  User,
  Users,
  Home,
  ArrowLeft,
  CheckCircle2,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import JobCard, { Listing } from '@/components/JobCard';
import SaveLeadButton from '@/components/SaveLeadButton';
import CheckoutModal from '@/components/CheckoutModal';
import LeadPurchaseBar from '@/components/LeadPurchaseBar';
// The post-purchase destination: confirmation + approved contact + next steps.
import LeadUnlockedPanel from '@/components/LeadUnlockedPanel';
import { MarketplaceDisclaimer } from '@/components/MarketplaceDisclaimer';



import PhotoGallery from '@/components/PhotoGallery';
import {
  Contact,
  claimLead,
  fetchContact,
  fetchFullListing,
  fetchListings,
  fetchPublicListing,
  fetchPurchasedIds,
  isSoldOut,
  recordLeadView,
  recordPurchase,
} from '@/lib/marketplace';

import { getLeadBalance, EMPTY_BALANCE, LeadBalance } from '@/lib/leadBalance';
// Announces server-side balance changes to the header pill and every other
// balance surface. Never used to compute a balance locally.
import { creditsChanged } from '@/lib/creditsBus';

import { canBuyLeads } from '@/lib/roles';
import { coverImageFor, normalizePhotos, tradeCoverFor, PROJECT_PLACEHOLDER } from '@/lib/coverImage';


import { LeadSettlement, clearIntent, readIntent, settleLeadPayment, isVerificationGate } from '@/lib/singleLead';
import { redactListing } from '@/lib/redact';
// Server-authoritative mailbox proof. The purchase gate asks these at click
// time instead of trusting the cached profile column (see handleUnlockClick).
import { fetchVerification, resendConfirmation } from '@/lib/accountState';


import { locationLabel, parseDocuments, safeFileName, ListingDocument } from '@/lib/listingSettings';
import { money, tradeLabel, canonicalSlug, localizedSlug, LEAD_PRICE } from '@/data/constants';




const JobDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, path, lang } = useI18n();
  const { profile, refreshProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState<Listing | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [documents, setDocuments] = useState<ListingDocument[]>([]);
  const [docBusy, setDocBusy] = useState('');
  const [docError, setDocError] = useState('');
  const [similar, setSimilar] = useState<Listing[]>([]);
  const [checkout, setCheckout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState('');
  /**
   * Outcome of a completed purchase:
   *   'unlocked'  this exact lead is now owned (the normal outcome for BOTH
   *               the $29 single purchase and a membership credit)
   *   'credited'  the project filled up mid-payment, so the credit is banked
   *   ''          nothing has been bought in this session
   */
  const [paidFlash, setPaidFlash] = useState('');
  /** True while the entitled contact read is in flight (see revealWithRetry). */
  const [revealing, setRevealing] = useState(false);
  const [balance, setBalance] = useState<LeadBalance>(EMPTY_BALANCE);
  /** A one-tap credit unlock is running (button shows progress, repeats ignored). */
  const [unlockBusy, setUnlockBusy] = useState(false);
  const unlockInFlight = useRef(false);
  /** The current flash message is a problem, not a success (amber, not green). */
  const [flashErr, setFlashErr] = useState(false);

  /**
   * Set ONLY when the SERVER has just confirmed the mailbox really is
   * unproven. It renders an actionable panel in place of the old dead-end
   * message that pointed at a confirmation link which never existed.
   */
  const [verifyBlocked, setVerifyBlocked] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyNote, setVerifyNote] = useState('');
  /** Which identity ('anon' or a user id) the last full load was done for. */
  const [checkedFor, setCheckedFor] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();



  /**
   * Documents live in a PRIVATE bucket. The server re-checks ownership /
   * purchase / admin before minting a 5-minute signed URL, so copying a path
   * gives an unentitled user nothing.
   */
  const openDocument = async (doc: ListingDocument) => {
    if (!job) return;
    setDocError('');
    setDocBusy(doc.path);
    const { data, error } = await supabase.functions.invoke('listing-media', {
      body: { action: 'sign-download', listingId: job.id, path: doc.path },
    });
    setDocBusy('');
    if (error || !data?.url) {
      setDocError(t('docs.errLocked'));
      return;
    }
    window.open(data.url, '_blank', 'noopener,noreferrer');
  };


  /**
   * Reveal the paid view (exact address, documents, customer contact).
   *
   * HARDENED 2026-09-22 after "anybody could see my leads".
   *
   * This used to infer the entitlement: it called fetchFullListing() and, if
   * ANY row came back, set unlocked=true and rendered the contact card. That
   * made the privacy of every lead depend on a single implicit condition —
   * "RLS returned nothing" — with no second opinion. Any future policy edit,
   * any view swapped in behind that helper, or any admin/service path reusing
   * it would silently expose the customer's name, phone, email and street
   * address to a contractor who had not paid.
   *
   * The entitlement is now PROVEN before anything is revealed:
   *   owner  — the listing belongs to this account, or
   *   buyer  — a row exists in mkt_lead_purchases for (listing, this buyer), or
   *   admin  — an operator account.
   *
   * Both checks still run against RLS-protected tables, so this is a second
   * independent gate rather than a replacement for the database rules. If the
   * proof fails we return false and the page stays in its locked state, no
   * matter what the listing query returned.
   */
  const loadUnlocked = async (listing: Listing) => {
    if (!profile?.id) return false;

    const full = await fetchFullListing(listing.id);
    if (!full) return false;

    const owns = full.owner_id === profile.id || listing.owner_id === profile.id;
    const purchased = owns ? true : (await fetchPurchasedIds(profile.id)).includes(listing.id);
    const entitled = owns || purchased || profile.is_admin === true;
    if (!entitled) {
      // Full row readable but no proven entitlement: treat as LOCKED and do
      // not fetch the contact record at all.
      return false;
    }

    setJob(full);
    setDocuments(parseDocuments(full.documents));
    setContact(await fetchContact(listing.id));
    setUnlocked(true);
    return true;
  };

  /**
   * REVEAL WITH RETRY — the post-purchase read.
   *
   * A purchase row is written inside the settlement transaction, but the read
   * that proves entitlement is a separate round trip. On a slow connection (or
   * against a read replica) the very first attempt can still come back empty,
   * which used to paint an EMPTY contact box directly under a "lead unlocked"
   * banner — the single most alarming thing a paying customer can see.
   *
   * So it retries with a short backoff and reports progress through
   * `revealing`, and the panel offers a manual retry if every attempt fails.
   * The entitlement itself is never assumed: loadUnlocked still has to prove
   * ownership or a purchase row before anything is displayed.
   */
  const revealWithRetry = async (listing: Listing, attempts = 3) => {
    setRevealing(true);
    try {
      for (let i = 0; i < Math.max(1, attempts); i += 1) {
        if (await loadUnlocked(listing)) return true;
        // 400ms, 800ms, 1200ms … long enough to clear replication lag, short
        // enough that nobody thinks the page has frozen.
        await new Promise((r) => window.setTimeout(r, 400 * (i + 1)));
      }
      return false;
    } finally {
      setRevealing(false);
    }
  };

  /**
   * Bring the confirmation into view. After paying on a phone the customer is
   * usually scrolled half way down the page, and a banner painted above the
   * fold would simply never be seen — "did it work?".
   */
  const scrollToConfirmation = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  };



  useEffect(() => {
    let active = true;
    setLoading(true);
    setUnlocked(false);
    setContact(null);
    setDocuments([]);

    const load = async () => {
      try {
        const dbSlug = canonicalSlug(slug || '');
        // Always start from the masked public view so anonymous visitors and
        // subcontractors who have not paid still get title/category/city/state.
        const masked = await fetchPublicListing(dbSlug);
        if (!active) return;
        /**
         * RENDER GUARD (defence in depth, P0 2026-09-22).
         *
         * `browse-listings` already strips phone numbers, emails, URLs and
         * street addresses out of the free text server-side, so this payload
         * should arrive clean. Re-applying the identical rules here means a
         * stale cached response, an offline replay, or any future code path
         * that reaches this page with unscrubbed text still cannot paint the
         * customer's number on a lead nobody has bought.
         *
         * redactPublic is idempotent, so scrubbing twice changes nothing.
         *
         * IMPORTANT: this only ever touches the LOCKED view. loadUnlocked()
         * below replaces `job` with the full, unmodified row once entitlement
         * is proven — a contractor who paid sees exactly what was written.
         */
        const safe = masked ? (redactListing(masked, lang) as Listing) : null;
        setJob(safe);
        // Leave the loading state the moment the primary record resolves — the
        // balance / entitlement / similar-projects calls below are secondary and
        // must never be able to hold the page on a spinner.
        setLoading(false);

        if (profile?.id) {
          const b = await getLeadBalance(profile.id);
          if (active) setBalance(b);
        }

        if (masked) {
          if (profile?.id) await loadUnlocked(masked);

          /**
           * "VIEWED LEAD" ACTIVITY -> GoHighLevel.
           * Fired once per lead per session (crmActivityOnce dedupes), only for
           * signed-in users — an anonymous visitor has no CRM identity yet.
           * Never awaited into the render path: it must not delay the page.
           */
          if (profile?.email) {
            void recordLeadView({
              listing: masked,
              viewerEmail: profile.email,
              viewerName: profile.company_name || profile.contact_name || undefined,
              viewerPhone: profile.phone || undefined,
              viewerType: profile.user_type,
              viewerId: profile.id,
              lang,
            });
          }

          const near = await fetchListings({ trade: masked.trade, limit: 4 });
          if (active) setSimilar(near.filter((x) => x.id !== masked.id).slice(0, 3));
        }

      } catch (error) {
        console.error('[JobDetail] Failed to load listing:', error);
        if (!active) return;
        setJob(null);
      } finally {
        // Guarantees the detail page can never be stranded on "Loading…".
        if (active) {
          setLoading(false);
          // Entitlement / balance are now known for THIS identity — the
          // auto-resume below waits for exactly this signal.
          setCheckedFor(profile?.id || 'anon');
        }
      }
    };
    load();

    return () => {
      active = false;
    };
  }, [slug, profile?.id]);


  /**
   * Unlock the lead.
   *
   * The browser no longer writes the entitlement. `claimLead` calls the
   * SECURITY DEFINER function `mkt_claim_lead`, which re-derives the buyer from
   * the JWT, re-checks role/status/listing, consumes exactly one included lead
   * under a row lock and inserts the purchase — all in one transaction, with a
   * unique index making duplicates impossible. A crafted REST call cannot grant
   * itself access because mkt_lead_purchases has no client INSERT policy.
   */
  const finalizeUnlock = async () => {
    // Double-tap guard: one credit spend in flight at a time. The server is
    // idempotent too (already_owned), this just keeps the UI honest.
    if (!job || !profile || unlockInFlight.current) return;
    unlockInFlight.current = true;
    setUnlockBusy(true);
    try {
      await finalizeUnlockCore();
    } finally {
      unlockInFlight.current = false;
      setUnlockBusy(false);
    }
  };

  const finalizeUnlockCore = async () => {
    if (!job || !profile) return;
    setFlash('');
    setFlashErr(false);

    const res = await claimLead(job.id);

    if (!res.ok) {
      setFlashErr(true);
      setCheckout(false);
      if (res.reason === 'sold_out') {
        // Capacity was reached while this page was open. The database already
        // flipped the listing to sold_out; reflect that immediately.
        setJob((j) => (j ? { ...j, status: 'sold_out', contractor_count: res.slotsTaken } : j));
        setFlash(t('leads.soldOutFlash'));
      } else if (
        res.reason === 'no_credits' ||
        res.reason === 'allowance_exhausted' ||
        res.reason === 'plan_expired' ||
        res.reason === 'no_active_plan'
      ) {
        /*
          NO DEAD END (2026-09-30 review). The page believed a credit was
          available but the server says none can be spent (used on another
          device, plan lapsed). This used to stop at an amber message and the
          buyer had to work out what to do next. Now the button flips to
          "Buy This Lead — $29" and the $29 checkout opens straight away, with
          the plan explanation as its note. The real balance is re-read from
          the server right after.
        */
        setBalance((b) => ({
          ...b,
          used: b.included,
          remaining: 0,
          spendable: 0,
          exhausted: b.included > 0,
        }));
        setFlash(balance.hasSubscription ? t('leads.noneLeft') : t('pay.leadNeedsPlan'));
        setCheckout(true);
        if (profile?.id) void getLeadBalance(profile.id).then(setBalance).catch(() => undefined);
      } else if (isVerificationGate(res.reason)) {
        // SERVER-SIDE GATE (mkt_claim_lead_core → mkt_purchase_gate): no credit was spent.
        setFlash('');
        setVerifyBlocked(true);
      } else {
        setFlash(t('jobs.unlockFailed'));
      }
      return;
    }

    setBalance((b) => ({
      ...b,
      used: Math.min(b.included, b.used + 1),
      remaining: res.remaining,
      spendable: Math.max(0, b.spendable - 1),
      exhausted: res.remaining === 0,
    }));
    await refreshProfile();
    // The credit was deducted server-side; re-read it everywhere.
    if (profile?.id) setBalance(await getLeadBalance(profile.id));
    creditsChanged('lead-unlock');


    // CRM bookkeeping only — the entitlement already exists server-side.
    // buyerType MUST be the real role: GCs buy leads too, and sending them as
    // 'subcontractor' put them in the wrong CRM pipeline.
    await recordPurchase({
      listing: job,
      buyerId: profile.id,
      amount: LEAD_PRICE,
      buyerEmail: profile.email,
      buyerName: profile.company_name || profile.contact_name || undefined,
      buyerPhone: profile.phone || undefined,
      buyerType: profile.user_type,
      lang,
    });

    // The purchase row now satisfies the RLS read policy, so the full record
    // (city, ZIP, documents, contact) becomes readable. Retry briefly: a fresh
    // entitlement can take a moment to be visible to the read path, and an
    // empty contact block after a successful unlock reads as a failure.
    setCheckout(false);
    clearPurchaseIntent();
    setPaidFlash('unlocked');
    scrollToConfirmation();
    await revealWithRetry(job, 4);
    setFlash(res.reason === 'already_owned' ? t('jobs.unlocked') : t('pay.usedIncluded'));
  };

  /**
   * A $29 SINGLE-LEAD PAYMENT SETTLED.
   *
   * THE PAYMENT BUYS THIS LEAD. The selection is stored on the order at intent
   * time and the server mints + spends the credit on THAT listing inside one
   * transaction, so `s.unlocked` is the normal outcome and this page must show
   * the unlocked lead immediately — the customer is already looking at the
   * project they paid for, so there is nothing to go and find.
   *
   * `s.unlocked === false` is the EXCEPTION: the project filled its 6th slot
   * while the card was being charged, so the credit is banked instead. Nothing
   * is lost and the choice is offered explicitly.
   */
  const handleLeadPaid = async (s: LeadSettlement) => {
    clearIntent();
    clearPurchaseIntent();
    // The modal is closed FIRST so the confirmation is never hidden behind an
    // overlay — a stuck modal after a successful payment is exactly what makes
    // people ask "did it work?".
    setCheckout(false);
    await refreshProfile();
    if (profile?.id) setBalance(await getLeadBalance(profile.id));
    // Tell the header pill (and any other balance surface, in this tab or
    // another) to re-read the server. Nothing is computed locally.
    creditsChanged('lead-purchase');

    if (s.unlocked) {
      // Show the confirmation immediately, then fill in the contact details as
      // the entitled read resolves. The panel has its own loading and retry
      // states, so the customer always sees progress rather than a blank.
      setPaidFlash('unlocked');
      scrollToConfirmation();
      await revealWithRetry(job as Listing, 4);
      return;
    }

    // Capacity was reached mid-payment: reflect it and bank the credit.
    if (s.slotsMax > 0 && s.slotsTaken >= s.slotsMax) {
      setJob((j) => (j ? { ...j, status: 'sold_out', contractor_count: s.slotsTaken } : j));
    }
    setPaidFlash('credited');
    scrollToConfirmation();
  };



  /**
   * RESUME AN INTERRUPTED PAYMENT. If the customer was interrupted after the
   * card was charged (backgrounded Safari, reload, dead battery), settle the
   * remembered payment on load. Settlement is idempotent server-side, so this
   * can never issue a second credit or a second entitlement.
   */
  useEffect(() => {
    if (!job?.id || !profile?.id || unlocked) return;
    const ticket = readIntent(job.id);
    if (!ticket) return;
    let active = true;
    (async () => {
      const s = await settleLeadPayment(ticket.paymentIntentId, job.id, lang);
      if (!active) return;
      if (s.ok) await handleLeadPaid(s);
      else if (s.error === 'payment_not_succeeded') clearIntent();
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, profile?.id, unlocked]);

  /** `direct` = the customer tapped the visible Buy/Unlock button themselves. */
  const handleUnlockClick = async (direct = false) => {
    if (!profile) {
      /*
        NEW CUSTOMER PATH. Most people pressing Buy here have no account yet,
        so they go to SIGN-UP (not sign-in), carrying this exact lead with
        them twice over: in ?next= and in localStorage (survives a new tab,
        the email link, or wandering off). Sign-up then returns them to
        /jobs/<slug>?buy=1, which re-opens checkout automatically.
      */
      const leadPath = window.location.pathname;
      const returnTo = leadReturnPath(leadPath);
      savePurchaseIntent({
        kind: 'lead',
        slug: slug || '',
        returnTo,
        label: job ? `${tradeLabel(job.trade, lang)} — ${locationLabel({ city: job.city, state: job.state })}` : undefined,
        // Sign-up pre-ticks this trade, one less thing to pick.
        trade: job?.trade || undefined,
      });
      navigate(authLink(path('/register'), returnTo, 'lead'));
      return;
    }
    /*
      WHO MAY UNLOCK: BOTH contractor-side roles.

      canBuyLeads() is true for 'gc' AND 'subcontractor', and the server agrees
      — mkt_claim_lead_core returns 'not_contractor' only when user_type is
      NOT IN ('subcontractor','gc'). So the ONLY role blocked here is
      homeowner, and the message must say that rather than implying general
      contractors are shut out. The same rule is enforced server-side, so
      hiding the button is a convenience, not the control.
    */
    if (!canBuyLeads(profile.user_type)) {
      setFlashErr(true);
      setFlash(t('jobs.roleCannotUnlock'));
      return;
    }
    /**
     * MAILBOX PROOF — ASKED LIVE, NEVER READ FROM A STALE COPY.
     *
     * P0 FIX 2026-09-22: "I received 'Account activated' and 'Your account is
     * now active', but buying a lead says 'Confirm your email address' and
     * there is nothing to confirm."
     *
     * Both statements were true at once. This project's auth provider
     * AUTO-CONFIRMS addresses (auth.users.email_confirmed_at is stamped in the
     * same second the account is created), so no confirmation link is ever
     * generated — there was genuinely nothing to click. But
     * mkt_profiles.email_verification_state defaults to 'unverified' and the
     * only writer that promotes it ran on SIGN-IN only, fire-and-forget, after
     * the profile had already been loaded. A contractor who registered and
     * went straight to a lead therefore carried 'unverified' in the cached
     * profile object for the whole session, and this gate refused them.
     *
     * Three changes close it: the profile row is now born 'verified' when the
     * provider has already confirmed (DB trigger), loadProfile() self-heals a
     * stale flag, and this gate asks the SERVER for the current answer instead
     * of trusting the copy in memory. Only a genuine, server-confirmed
     * 'unverified' stops the purchase now — and when it does, it offers the
     * action inline rather than sending people to the sign-in page to look for
     * a link that does not exist.
     *
     * ('legacy_unverified' predates enforcement and is deliberately NOT
     * blocked; retro-blocking would lock out every existing paying
     * contractor.)
     *
     * This remains a convenience gate. The contact record itself is protected
     * by the RLS policy p_contacts_read (owner, proven purchase, or admin) and
     * by the entitlement proof in loadUnlocked, neither of which the browser
     * can talk its way past.
     */
    if (profile.email_verification_state === 'unverified') {
      setVerifyBusy(true);
      const live = await fetchVerification(true);
      setVerifyBusy(false);
      if (live.verified || live.legacy) {
        // The cached flag was stale; the server has now confirmed the mailbox.
        await refreshProfile();
      } else {
        setFlash('');
        setVerifyBlocked(true);
        return;
      }
    }
    setVerifyBlocked(false);
    /*
      ONE TAP WITH A CREDIT. A member tapping "Unlock for 1 Credit" used to get
      a $29 checkout modal with the credit option tucked above a card form
      ("am I being charged?"). The button already states exactly what happens,
      so a direct tap spends the credit straight away. The auto-resume after
      sign-up still opens the modal, because the buyer never saw that label.
    */
    if (direct && balance.spendable > 0) {
      await finalizeUnlock();
      return;
    }
    setCheckout(true);
  };

  /**
   * Last-resort recovery for a genuinely unproven mailbox. Sends whatever the
   * server decides is appropriate — the provider confirmation mail if one can
   * exist, otherwise the branded account email — so the person always has a
   * real action to take instead of a dead end.
   */
  const handleSendProof = async () => {
    if (!profile) return;
    setVerifyBusy(true);
    const res = await resendConfirmation(
      profile.email,
      lang,
      profile.id,
      profile.company_name || profile.contact_name || undefined,
    );
    const live = await fetchVerification(true);
    setVerifyBusy(false);
    if (live.verified || live.legacy) {
      setVerifyBlocked(false);
      await refreshProfile();
      setFlashErr(false);
      setFlash(lang === 'es' ? 'Cuenta verificada. Ya puede comprar leads.' : 'Account verified. You can buy leads now.');
      return;
    }
    setVerifyNote(
      res.rateLimited
        ? lang === 'es'
          ? 'Demasiados intentos. Espere unos minutos.'
          : 'Too many attempts. Please wait a few minutes.'
        : res.ok
        ? lang === 'es'
          ? 'Correo enviado. Abra el enlace y vuelva a intentarlo.'
          : 'Email sent. Open the link, then try again.'
        : lang === 'es'
        ? 'No se pudo enviar. Contáctenos y lo resolvemos.'
        : 'Could not send. Contact us and we will clear it manually.',
    );
  };

  /**
   * AUTO-CONTINUE AFTER EMAIL CONFIRMATION.
   * While the "confirm your email" step is showing, re-ask the server every
   * 5 seconds and whenever this tab regains focus (the customer coming back
   * from their inbox). The moment the mailbox is proven, checkout opens by
   * itself — no "now go back and press Buy again".
   */
  useEffect(() => {
    if (!verifyBlocked || !profile?.id) return;
    // On phones the panel sits below the fold under the sticky bar; bring it
    // into view so tapping Buy never looks like "nothing happened".
    window.setTimeout(() => {
      document.getElementById('gg-verify-panel')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    let stopped = false;
    let ticks = 0;
    const check = async () => {
      if (stopped) return;
      const live = await fetchVerification(false);
      if (stopped) return;
      if (live.verified || live.legacy) {
        stopped = true;
        window.clearInterval(timer);
        setVerifyBlocked(false);
        setVerifyNote('');
        await refreshProfile();
        setFlashErr(false);
        setFlash(lang === 'es' ? 'Correo confirmado. Abriendo el pago…' : 'Email confirmed. Opening checkout…');
        setCheckout(true);
      }
    };
    const timer = window.setInterval(() => {
      ticks += 1;
      if (ticks > 120) window.clearInterval(timer); // stop after ~10 minutes
      void check();
    }, 5000);
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyBlocked, profile?.id]);

  /**
   * RESUME THE PURCHASE AFTER SIGN-UP / SIGN-IN.
   * The auth pages send the buyer back to /jobs/<slug>?buy=1. Once the page
   * knows who they are and whether they already own this lead, the Buy step
   * runs by itself, exactly as if they had pressed the button — so a new
   * customer lands straight in checkout for the lead they chose.
   * Runs once; the ?buy flag is removed so Back / refresh never re-opens it.
   */
  const autoBuyRan = useRef(false);
  useEffect(() => {
    if (autoBuyRan.current || searchParams.get('buy') !== '1') return;
    if (authLoading || loading || !job) return;
    // Wait until entitlement has been checked for THIS identity.
    if (checkedFor !== (profile?.id || 'anon')) return;
    autoBuyRan.current = true;
    const p = new URLSearchParams(searchParams);
    p.delete('buy');
    setSearchParams(p, { replace: true });
    if (!profile) return; // still signed out: the normal Buy button remains
    if (unlocked || isSoldOut(job)) {
      clearPurchaseIntent();
      return;
    }
    void handleUnlockClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, authLoading, loading, job?.id, checkedFor, profile?.id, unlocked]);

  /** Already owned (e.g. bought on another device): nothing left to resume. */
  useEffect(() => {
    if (unlocked && job) {
      const i = readPurchaseIntent();
      if (i?.kind === 'lead' && samePage(i.returnTo, window.location.pathname)) clearPurchaseIntent();
    }
  }, [unlocked, job?.id]);

  /**
   * AUTO-UNLOCK AFTER A HOSTED $29 PAYMENT  (2026-09-30 journey review).
   *
   * The hosted checkout grants ONE credit and /payment-complete sends the buyer
   * back here with ?unlock=1. Previously they then had to find and tap
   * "Unlock for 1 Credit" and confirm again in a modal — two extra steps for a
   * lead they had literally just paid for. Now the credit is spent on THIS lead
   * automatically (the same server-side mkt_claim_lead call as the button, so
   * sold-out / verification / no-credit rules still apply), and the "Lead
   * unlocked" panel with the contact details appears by itself.
   */
  const autoUnlockRan = useRef(false);
  useEffect(() => {
    if (autoUnlockRan.current || searchParams.get('unlock') !== '1') return;
    if (authLoading || loading || !job || !profile) return;
    if (checkedFor !== profile.id) return;
    autoUnlockRan.current = true;
    const p = new URLSearchParams(searchParams);
    p.delete('unlock');
    setSearchParams(p, { replace: true });
    if (unlocked) {
      // The webhook already spent the credit on this lead — just confirm it.
      clearPurchaseIntent();
      setPaidFlash('unlocked');
      scrollToConfirmation();
      return;
    }
    if (isSoldOut(job)) {
      // Filled up while paying: the credit is safe in the balance.
      if (balance.spendable > 0) {
        setPaidFlash('credited');
        scrollToConfirmation();
      }
      return;
    }
    if (balance.spendable > 0) void handleUnlockClick(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, authLoading, loading, job?.id, checkedFor, profile?.id, unlocked, balance.spendable]);

  /**
   * PROBLEMS MUST BE SEEN (mobile). On a phone the buyer taps the sticky bar
   * at the BOTTOM of the screen, but problem messages ("this lead just filled
   * up", "project owner accounts cannot buy leads", "could not unlock") are
   * painted at the TOP of the page — so the tap looked like it did nothing.
   * Bring any problem message into view the moment it appears.
   */
  useEffect(() => {
    if (!flash || !flashErr || checkout) return;
    const id = window.setTimeout(() => {
      document.getElementById('gg-lead-flash')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 40);
    return () => window.clearTimeout(id);
  }, [flash, flashErr, checkout]);




  if (loading) return <div className="mx-auto max-w-5xl px-4 py-20 text-center text-slate-500">{t('common.loading')}</div>;
  if (!job)
    return (
      <div className="mx-auto max-w-5xl px-4 py-20 text-center">
        <p className="text-slate-600">{t('jobs.notFound')}</p>
        <Link to={path('/jobs')} className="mt-4 inline-block font-bold text-orange-600">
          {t('jobs.title')}
        </Link>
      </div>
    );

  // normalizePhotos() tolerates every shape the row can arrive in (text[],
  // JSON string, object list, protocol-relative URL). The old
  // `(job.photos || []).filter(Boolean)` silently produced ZERO photos for a
  // payload that had travelled through the edge function as a JSON string.
  const photos = normalizePhotos(job.photos);
  // Priority: homeowner upload -> trade/category cover -> branded marketplace
  // cover. Never a gray tile, on any project, in any language.
  const cover = coverImageFor(job);

  // LOCATION PRIVACY: city + state ("Fontana, CA") are public on every preview.
  // Street address, ZIP, homeowner name, phone and email stay locked until the
  // lead is purchased — that is what `exactLocationLocked` gates.
  const exactLocationLocked = !unlocked;
  const place = locationLabel({ city: job.city, state: job.state });
  const docCount = unlocked ? documents.length : job.document_count ?? 0;
  // 6 of 6 contractor slots filled. A buyer who already owns the lead keeps
  // full access — only NEW unlocks are closed off.
  const soldOut = !unlocked && isSoldOut(job);



  return (
    /*
      MOBILE BOTTOM PADDING. The sticky purchase bar (below `lg`) is ~84px
      tall plus the safe-area inset, so the page reserves that space and the
      disclaimer / similar projects can never end up underneath it.
    */
    <div className="mx-auto max-w-6xl px-4 py-8 pb-32 sm:px-6 lg:px-8 lg:pb-8">

      <Seo
        title={`${tradeLabel(job.trade, lang)} — ${place}`}
        description={(job.description || job.title).slice(0, 160)}
        // Shared links preview THIS project's cover, not the generic brand card.
        image={cover.src}
        schema={{
          '@type': 'JobPosting',
          title: job.title,
          description: job.description || job.title,
          datePosted: job.created_at,
          employmentType: 'CONTRACTOR',
          industry: tradeLabel(job.trade, 'en'),
          inLanguage: lang,
          image: cover.src,

          hiringOrganization: { '@type': 'Organization', name: job.source_type === 'gc' ? 'General Contractor' : 'Homeowner' },
          jobLocation: {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              // Only publish the precise locality once it is public anyway.
              addressRegion: job.state,
              addressCountry: 'US',
            },
          },
        }}
      />

      <button
        onClick={() => navigate(path('/jobs'))}
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> {t('jobs.title')}
      </button>

      {/*
        PURCHASE RESULT. The $29 single purchase and a membership credit both
        finish here, so the confirmation is identical however it was paid for.

        'unlocked'  THE NORMAL OUTCOME. This exact lead is now owned. The panel
                    carries the approved contact details AND every next action
                    (Call, Email, My Leads, Browse more), so nobody has to find
                    the project again or wonder what happens next.
        'credited'  THE EXCEPTION. The project filled its 6th contractor slot
                    while the card was being charged, so the credit is banked
                    instead of spent. The money is not lost, and the choice is
                    offered explicitly rather than left as a mystery.
      */}
      {paidFlash === 'unlocked' && (
        <LeadUnlockedPanel
          contact={contact}
          revealing={revealing}
          onRetry={() => {
            if (job) void revealWithRetry(job, 1);
          }}
          dashboardHref={path('/my-leads')}

          jobsHref={path('/jobs')}
        />
      )}

      {paidFlash === 'credited' && (
        <div className="mb-6 rounded-2xl border-2 border-emerald-300 bg-emerald-50 px-5 py-4">
          <p className="flex items-center gap-2 text-base font-extrabold text-emerald-900">
            <CheckCircle2 className="h-5 w-5" /> {t('pay.creditedTitle')}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-emerald-800">{t('pay.creditedBody')}</p>
          <p className="mt-2 text-sm font-bold text-emerald-900">
            {t('pay.creditsInBalance')}: {balance.spendable}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!unlocked && !soldOut && (
              <button
                type="button"
                onClick={finalizeUnlock}
                style={{ touchAction: 'manipulation' }}
                className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"
              >
                {t('pay.unlockNowCta')}
              </button>
            )}
            <Link
              to={path('/jobs')}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl border-2 border-emerald-300 px-4 py-2.5 text-sm font-bold text-emerald-800 hover:bg-emerald-100"
            >
              {t('pay.saveCreditCta')}
            </Link>
          </div>
        </div>
      )}



      {flash && (
        /*
          Problems ("no credits left", "lead filled up", "could not unlock")
          used to appear in the same green box with a check mark as success,
          which reads as "it worked". Problems are amber now, and announced.
        */
        <div
          id="gg-lead-flash"
          role={flashErr ? 'alert' : 'status'}
          className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${
            flashErr ? 'border border-amber-300 bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {flashErr ? <Lock className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />} {flash}
        </div>
      )}


      <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
              {tradeLabel(job.trade, lang)}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-600">
              {job.project_type === 'commercial' ? t('common.commercial') : t('common.residential')}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-600">
              {job.source_type === 'gc' ? t('jobs.byGc') : t('jobs.byHomeowner')}
            </span>
          </div>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
            <h1 className="min-w-0 flex-1 text-3xl font-extrabold leading-tight tracking-tight text-slate-900">{job.title}</h1>
            {/* Bookmark only — does not unlock, buy or reserve this lead. */}
            <SaveLeadButton listingId={job.id} variant="inline" className="shrink-0" />
          </div>

          {/*
            IMAGE RULE (never a blank area):
            1. homeowner uploads → carousel
            2. no uploads → trade/category cover
            3. category cover itself fails → branded marketplace cover
            `fallbackCover` is always a real URL, and `secondaryCover` gives the
            gallery one more real photo to step to, so the hero can never be an
            empty box.
          */}
          <PhotoGallery
            photos={photos}
            alt={job.title}
            fallbackCover={cover.isRealPhoto ? tradeCoverFor(job) || undefined : cover.src}
            secondaryCover={PROJECT_PLACEHOLDER}
          />



          <dl className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
                <MapPin className="h-3.5 w-3.5" /> {t('common.location')}
              </dt>
              {/* City + state abbreviation, always. Never the street or ZIP. */}
              <dd className="mt-1 font-bold text-slate-900">{place || '—'}</dd>
              {exactLocationLocked && (
                <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-amber-600">
                  {t('jobs.cityStateOnly')}
                </dd>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
                <DollarSign className="h-3.5 w-3.5" /> {t('common.budget')}
              </dt>
              <dd className="mt-1 font-bold text-slate-900">{job.budget_range || '—'}</dd>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
                <CalendarDays className="h-3.5 w-3.5" /> {job.source_type === 'gc' ? t('jobs.dueDate') : t('jobs.startDate')}
              </dt>
              <dd className="mt-1 font-bold text-slate-900">{job.target_date || '—'}</dd>
            </div>
          </dl>

          {exactLocationLocked && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <p className="flex items-center gap-2 text-sm font-extrabold text-amber-900">
                <Lock className="h-4 w-4" /> {t('jobs.locationLockedTitle')}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-amber-800">{t('jobs.locationLockedBody')}</p>
            </div>
          )}


          <h2 className="mt-8 text-lg font-extrabold text-slate-900">{t('jobs.description')}</h2>
          <p className="mt-2 whitespace-pre-line leading-relaxed text-slate-600">{job.description}</p>

          {docCount > 0 && (
            <>
              <h2 className="mt-8 text-lg font-extrabold text-slate-900">{t('jobs.documents')}</h2>
              {unlocked ? (
                <ul className="mt-3 space-y-2">
                  {documents.map((d) => (
                    <li
                      key={d.path}
                      className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
                    >
                      <FileText className="h-5 w-5 shrink-0 text-red-500" />
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                        {safeFileName(d.name)}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                        {t(`docs.kind.${d.kind || 'other'}`)}
                      </span>
                      <button
                        type="button"
                        onClick={() => openDocument(d)}
                        disabled={docBusy === d.path}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-700 disabled:opacity-50"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> {docBusy === d.path ? t('common.loading') : t('docs.open')}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600">
                  <Lock className="h-4 w-4 text-slate-400" />
                  {docCount} {t('jobs.documentsLocked')}
                </p>
              )}
              {docError && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{docError}</p>}
            </>
          )}


          {similar.length > 0 && (
            <>
              <h2 className="mt-12 text-lg font-extrabold text-slate-900">{t('jobs.similar')}</h2>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                {similar.map((s) => (
                  <JobCard
                    key={s.id}
                    job={s}
                    credits={profile ? balance.spendable : undefined}
                    onUnlock={() => navigate(path(`/jobs/${localizedSlug(s.slug, s.trade, lang)}`))}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <aside>
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('jobs.leadPrice')}</p>
            <p className="mt-1 text-3xl font-extrabold text-slate-900">{money(LEAD_PRICE)}</p>
            <p className="mt-1 text-xs font-semibold text-slate-400">{t('pricing.fixedLeadNote')}</p>


            {unlocked && contact ? (
              <div className="mt-5 space-y-3 rounded-xl bg-emerald-50 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">{t('jobs.contactUnlocked')}</p>
                <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <User className="h-4 w-4 text-emerald-600" />
                  {contact.contact_name}
                </p>
                <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-emerald-700">
                  <Phone className="h-4 w-4 text-emerald-600" />
                  {contact.phone}
                </a>
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-2 break-all text-sm font-semibold text-slate-800 hover:text-emerald-700"
                >
                  <Mail className="h-4 w-4 text-emerald-600" />
                  {contact.email}
                </a>
                <p className="flex items-start gap-2 text-sm text-slate-700">
                  <Home className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  {contact.address}
                </p>
                <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">{t('jobs.unlocksNow')}</p>
              </div>
            ) : soldOut ? (
              /*
                CAPACITY REACHED. A lead is shared with at most 6 contractors;
                the database flipped this listing to sold_out inside the same
                locked transaction that granted the 6th slot, so no further
                unlock is possible and no credit can be deducted. The unlock
                button is replaced (not merely disabled) so there is no path
                that could spend a credit on a full lead.
              */
              <div className="mt-5 rounded-xl border border-slate-300 bg-slate-50 p-4">
                <p className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-700">
                  <Users className="h-4 w-4" /> {t('leads.soldOutTitle')}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t('leads.soldOutBody')}</p>
                <Link
                  to={path('/jobs')}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white hover:bg-slate-800"
                >
                  {t('leads.soldOutCta')}
                </Link>
              </div>
            ) : (
              <>
                <p className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> {t('jobs.contactLocked')}
                </p>
                {exactLocationLocked && (
                  <p className="mt-2 flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800">
                    <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t('jobs.exactLocationHidden')}
                  </p>
                )}

                <button
                  type="button"
                  onClick={() => void handleUnlockClick(true)}
                  disabled={unlockBusy}
                  style={{ touchAction: 'manipulation' }}
                  className="mt-4 min-h-[52px] w-full rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300 disabled:opacity-60"
                >
                  {/*
                    THE ACTION IS ALWAYS EXPLICIT AND HONEST:
                    with a credit  -> "Unlock for 1 Credit" (ONE tap, no modal)
                    without one    -> "Buy This Lead — $29"
                  */}
                  {unlockBusy
                    ? t('common.loading')
                    : balance.spendable > 0
                    ? t('jobs.unlockWithCredit')
                    : `${t('pay.buyThisLead')} — ${money(LEAD_PRICE)}`}
                </button>
                <p className="mt-2 text-center text-[11px] font-semibold text-slate-400">
                  {/* Buying tops the balance up by one — it does not spend it. */}
                  {balance.spendable > 0 ? t('leads.oneCreditNote') : t('pay.creditNote')}
                </p>

                {/*
                  MAILBOX PROOF PANEL — shown ONLY when the server has just
                  re-checked and the address genuinely is unproven. The old
                  behaviour was a one-line message telling people to "resend the
                  confirmation from the sign-in page", where no such link exists
                  on this auth provider — a dead end. This panel performs the
                  recovery in place and re-tests immediately afterwards.
                */}
                {verifyBlocked && (
                  /*
                    ONE LAST STEP — plain words, the address shown, and NO need
                    to come back and click again: this page checks every few
                    seconds (and the moment the tab regains focus) and opens
                    checkout by itself once the link has been opened.
                  */
                  <div id="gg-verify-panel" className="mt-4 scroll-mt-24 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
                    <p className="text-sm font-extrabold text-amber-900">
                      {lang === 'es' ? 'Último paso: confirme su correo' : 'Last step: confirm your email'}
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-amber-800">
                      {lang === 'es' ? 'Le enviamos un enlace a ' : 'We emailed a link to '}
                      <span className="font-bold break-all">{profile?.email}</span>.{' '}
                      {lang === 'es'
                        ? 'Ábralo y esta página seguirá sola al pago. No cierre esta página.'
                        : 'Open it and this page will continue to payment by itself. Keep this page open.'}
                    </p>
                    <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-amber-900">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                      {lang === 'es' ? 'Esperando la confirmación…' : 'Waiting for confirmation…'}
                    </p>
                    <button
                      type="button"
                      onClick={handleSendProof}
                      disabled={verifyBusy}
                      style={{ touchAction: 'manipulation' }}
                      className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border-2 border-amber-400 bg-white px-4 py-2.5 text-sm font-bold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {verifyBusy
                        ? t('common.loading')
                        : lang === 'es'
                        ? '¿No llegó? Enviar otra vez'
                        : 'Didn’t get it? Send it again'}
                    </button>
                    <p className="mt-2 text-[11px] text-amber-800">
                      {lang === 'es' ? 'Revise también spam / correo no deseado.' : 'Check your spam / junk folder too.'}
                    </p>
                    {verifyNote && <p className="mt-2 text-xs font-semibold text-amber-900">{verifyNote}</p>}
                  </div>
                )}

                {!profile && <p className="mt-3 text-center text-xs text-slate-400">{t('jobs.loginToUnlock')}</p>}

              </>
            )}
            {/* The disclaimer text differs per role, so it must follow the
                VIEWER's role — a homeowner or GC reading this page was
                previously shown the subcontractor-only wording. Signed out
                (null) falls back to the neutral marketplace wording. */}
            <MarketplaceDisclaimer variant="short" role={profile?.user_type ?? null} className="mt-5" />
          </div>
        </aside>

      </div>

      <CheckoutModal
        open={checkout}
        onClose={() => setCheckout(false)}
        amount={LEAD_PRICE}

        title={t('pay.unlockTitle')}
        subtitle={t('pay.unlockSub')}
        lineItem={`${t('pay.leadFor')}: ${tradeLabel(job.trade, lang)} — ${place}`}
        plansHref={path('/pricing')}
        listingId={job.id}
        listingSlug={job.slug}
        onLeadPaid={handleLeadPaid}
        onVerificationRequired={() => {
          // Server-side gate refused the $29 intent (no charge was created).
          setFlash('');
          setVerifyBlocked(true);
        }}
        altAction={
          balance.spendable > 0
            ? {
                label: `${t('pay.useIncluded')} (${balance.spendable})`,
                run: () => finalizeUnlock(),
              }
            : undefined
        }

        note={
          balance.spendable > 0
            ? undefined
            : balance.hasSubscription
            ? t('leads.noneLeft')
            : t('pay.leadNeedsPlan')
        }
      />

      {/*
        MOBILE PURCHASE BAR — the fix for the reported "no purchase button on
        mobile" report. Below `lg` the sidebar card is the last block in the
        document, so this pinned bar carries the SAME handler and keeps the
        $29 CTA on screen at every scroll position and every screen width.
        It hides itself when the lead is already owned or sold out, and while
        the checkout modal is open.
      */}
      <LeadPurchaseBar
        unlocked={unlocked}
        soldOut={soldOut}
        credits={balance.spendable}
        onPurchase={() => void handleUnlockClick(true)}
      />
    </div>

  );
};

export default JobDetail;
